import React, {useCallback, useMemo} from 'react';
import {Profile} from '../../interfaces';
import {
  DataGrid,
  GridColDef,
  GridRowModel,
  GridToolbar,
} from '@mui/x-data-grid';
import {useNavigate} from 'react-router-dom';
import {Paper} from '@mui/material';
import {DateTime} from 'luxon';
import {useTranslation} from 'react-i18next';
import {TFunction} from 'i18next';

interface Props {
  profiles: Profile[];
  height?: number;
  loading?: boolean;
}

const formatDate = (value: unknown) =>
  value ? DateTime.fromISO(value as string).toFormat('yyyy-MM-dd') : '';

const getColumns = (t: TFunction): GridColDef[] => [
  {
    field: 'id',
    headerName: t('admin.shopifySubscriptionsTable.columns.id') as string,
    width: 90,
    filterable: true,
  },
  {
    field: 'firstName',
    headerName: t('profile.firstName') as string,
    width: 150,
    filterable: true,
  },
  {
    field: 'lastName',
    headerName: t('profile.lastName') as string,
    width: 150,
    filterable: true,
  },
  {
    field: 'email',
    headerName: t('general.email') as string,
    width: 280,
    filterable: true,
  },
  {
    field: 'role',
    headerName: t('admin.shopifySubscriptionDetail.fields.role') as string,
    width: 120,
    filterable: true,
  },
  {
    field: 'membershipLevel',
    headerName: t('membership.membershipLevel') as string,
    width: 160,
    filterable: true,
  },
  {
    field: 'membershipTrialEndDate',
    headerName: t(
      'admin.profileDetail.fields.membershipTrialEndDate',
    ) as string,
    width: 150,
    filterable: true,
    valueFormatter: params => formatDate(params.value),
  },
  {
    field: 'lastSessionDate',
    headerName: t(
      'admin.shopifySubscriptionDetail.fields.lastSession',
    ) as string,
    width: 150,
    filterable: true,
    valueFormatter: params => formatDate(params.value),
  },
];

const ProfilesTable: React.FC<Props> = ({
  profiles,
  height = 700,
  loading = false,
}) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const columns = useMemo(() => getColumns(t), [t]);

  const handleRowClick = useCallback(
    (row: GridRowModel) => {
      navigate(`/admin/profiles/${row.id}`);
    },
    [navigate],
  );

  return (
    <Paper sx={{height}}>
      <DataGrid
        rows={profiles ?? []}
        columns={columns}
        onRowClick={handleRowClick}
        loading={loading}
        components={{Toolbar: GridToolbar}}
        componentsProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: {debounceMs: 300},
          },
        }}
        disableSelectionOnClick
      />
    </Paper>
  );
};

export default React.memo(ProfilesTable);
